import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
  Typography,
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";
import { Grid } from "@mui/material";
import { AutoFixHighOutlined } from "@mui/icons-material";
import PatientPlanDetails from "./PatientPlanDetails";

interface PatientData {
  patientName: string;
  patientId: string;
  dateOfBirth: string;
  oldPlanName: string;
  newPlanName: string;
}

interface PatientPlan {
  id: string;
  patientName: string;
  patientId: string;
  datePlanRun: string;
  planName: string;
  technique: string;
  tumorStream: string;
}

const mockPatientData: PatientPlan[] = [
  {
    id: "1",
    patientName: "Jonathon Washington",
    patientId: "180033",
    datePlanRun: "11/08/2024",
    planName: "Prostate Nodes R2",
    technique: "IMRT",
    tumorStream: "Prostate Nodes",
  },
  {
    id: "2",
    patientName: "Michael Chang",
    patientId: "123456",
    datePlanRun: "11/08/2024",
    planName: "Prostate Nodes R2",
    technique: "VMAT",
    tumorStream: "Prostate Nodes",
  },
  {
    id: "3",
    patientName: "Oliver White",
    patientId: "789001",
    datePlanRun: "11/08/2024",
    planName: "Prostate Nodes R2",
    technique: "IMRT",
    tumorStream: "Prostate Nodes",
  },
];

const PlanGeneration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { recipeName } = useParams<{ recipeName: string }>();
  const patientData = location.state as PatientData;
  const [selectedPatient, setSelectedPatient] = useState<PatientPlan | null>(
    mockPatientData[0],
  );

  // Get the encoded recipe name for URLs
  const encodedRecipeName = encodeURIComponent(recipeName || "");

  const handleGenerateTestPlan = () => {
    const updatedPatientData = selectedPatient
      ? {
          patientName: selectedPatient.patientName,
          patientId: selectedPatient.patientId,
          dateOfBirth: "1965-03-15", // Keep for compatibility
          oldPlanName: selectedPatient.planName,
          newPlanName: "Prostate Nodes R3",
        }
      : patientData;

    navigate(`/blueprint/${encodedRecipeName}/tracking`, {
      state: updatedPatientData,
    });
  };

  const templateStructures = [
    { name: "PTV", required: true },
    { name: "CTV", required: true },
    { name: "Rectum", required: false },
    { name: "Bladder", required: false },
    { name: "Femur_L", required: false },
    { name: "Femur_R", required: false },
    { name: "PenileBulb", required: false },
  ];

  const availableStructures = [
    "PTV",
    "CTV",
    "Rectum",
    "Bladder",
    "Femur_Combined",
    "Femur_Combined",
    "PenileBulb",
  ];

  return (
    <Stack height={1} gap={2}>
      {/* Test RT structure set section */}
      <Box>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body1">Test RT structure set *</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              value={selectedPatient?.id || ""}
              onChange={(e) => {
                const patient = mockPatientData.find(
                  (p) => p.id === e.target.value,
                );
                setSelectedPatient(patient || null);
              }}
              variant="outlined"
              size="small"
            >
              <MenuItem value="" disabled>
                <Typography color="text.secondary">Select a patient</Typography>
              </MenuItem>
              {mockPatientData.map((patient) => (
                <MenuItem key={patient.id} value={patient.id}>
                  <Box>
                    <Typography variant="body1">
                      {patient.patientName} | Pat ID: {patient.patientId}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Plan - {patient.planName}; RTStruct -{" "}
                      {patient.tumorStream}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Last Run: {patient.datePlanRun}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Patient and Plan Details */}
      {selectedPatient && (
        <PatientPlanDetails
          patientData={{
            patientName: selectedPatient.patientName,
            patientId: selectedPatient.patientId,
            dateOfBirth: "1965-03-15", // Keep for compatibility
            oldPlanName: selectedPatient.planName,
            newPlanName: "Stereotactic Prostate",
          }}
        />
      )}

      {/* Map Structure section */}
      <Box>
        <Typography variant="h6" component="h3" gutterBottom>
          Map Structure
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Structures marked with * are mandatory. Unmapped template structure
          objectives will be removed for planning automation upon submission.
        </Typography>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" gutterBottom>
              Template structure
            </Typography>
            <Stack spacing={2}>
              {templateStructures.map((structure) => (
                <Box key={structure.name} sx={{ py: 1 }}>
                  <Typography variant="body1" color="text.secondary">
                    {structure.name} {structure.required ? "*" : ""}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" gutterBottom>
              Available structure
            </Typography>
            <Stack spacing={2}>
              {availableStructures.map((structure) => (
                <TextField
                  key={structure}
                  select
                  fullWidth
                  value={structure}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  <MenuItem value={structure}>{structure}</MenuItem>
                </TextField>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Buttons */}
      <Stack direction="row" justifyContent="space-between" marginTop={"auto"}>
        <Button
          component={Link}
          to={`/blueprint/${encodedRecipeName}`}
          variant="outlined"
          color="primary"
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateTestPlan}
          startIcon={<AutoFixHighOutlined />}
          disabled={!selectedPatient}
        >
          Generate Test Plan
        </Button>
      </Stack>
    </Stack>
  );
};

export default PlanGeneration;
